<template>
  <v-app id="app">
    <WorkMaintenanceInformation
      ref="maintenanceInformation"
    />

    <WorkHeaderWrapper
      v-if="isInternalPath"
      disable-message-add-button
    />

    <div
      id="mainContent"
      :class="getMainClass()"
      :style="{ '--maintenance-height': `${maintenanceInformationHeight}px` }"
      @dragover.prevent
      @drop.prevent
    >
      <WorkErrorNotification />

      <v-container
        :class="{
          'work-main-container pa-0': true,
          'work-main-container--desktop': $vuetify.breakpoint.mdAndUp
        }"
        fluid
      >
        <w-row class="work-main-row pr-0 ma-0">
          <w-column
            v-show="isAuthenticated
              && ($vuetify.breakpoint.mdAndUp || ($vuetify.breakpoint.smAndDown && isSidebarDrawerOpen))"
            cols="auto"
            :class="{
              'work-main-col pa-0': true,
              'work-main-col_sidebar--mobile': $vuetify.breakpoint.smAndDown,
            }"
          >
            <WorkSidebar v-if="isInternalPath" />
          </w-column>
          <w-column
            v-show="($vuetify.breakpoint.mdAndUp || ($vuetify.breakpoint.smAndDown && !isSidebarDrawerOpen))"
            :class="{
              'work-main-col': true,
              'work-main-col_content': isAuthenticated,
              'pa-0': !isAuthenticated
            }"
          >
            <div class="work-main-content-wrapper">
              <router-view
                :key="$route.fullPath"
                ref="routerView"
              />
            </div>
          </w-column>
        </w-row>
      </v-container>

      <WorkBeforeLeaveDialog ref="beforeLeaveDialog" />
      <WorkGlobalWarningDialog ref="warningDialog" />
      <WorkGlobalSnackbar />
    </div>
  </v-app>
</template>

<script>
import authMixin from '@/mixins/authentication-mixin.js';
import cookies from 'vue-cookies'
import { mapActions, mapState, mapGetters } from 'vuex';
import dynamicData from '@/mixins/api_data/dynamic-data-mixin';
import WorkBeforeLeaveDialog from '@/components/global_components/dialogs/WorkBeforeLeaveDialog';
import WorkGlobalWarningDialog from '@/components/global_components/dialogs/WorkGlobalWarningDialog.vue';
import WorkHeaderWrapper from '@/components/header_components/WorkHeaderWrapper';
import WorkSidebar from '@/components/sidebar_components/WorkSidebar.vue';
import store from "@/store";

const CUSTOM_PRIMARY_COLORS = (useTsTheme) => (useTsTheme ? '#1b91e6' : '#8CAE0E');
const CUSTOM_HEADER_COLORS = (useTsTheme) => (useTsTheme ? '#062b3b' : '#232330');
const CUSTOM_LOGIN_BACKGROUND_COLOR = (useTsTheme) => (useTsTheme ? '#062b3b' : '#3f3f4a');
const CUSTOM_MAIN_BACKGROUND_COLOR = (useTsTheme, systemMode) => ((useTsTheme && systemMode == 'staging') ? '#7D1819' : '#232330');

const CUSTOM_LOGO_TEST_PATH = 'tool_service_work_test.svg';
const CUSTOM_LOGO_PROD_PATH = 'tool_service_combine.svg';
const CUSTOMER_INSTANCE_PATH = 'ts';

export default {
  name: 'App',
  components: {
    WorkHeaderWrapper,
    WorkErrorNotification: () =>
      import('@/components/alert_components/error/WorkErrorNotification'),
    WorkMaintenanceInformation: () =>
    import('@/components/alert_components/error/WorkMaintenanceInformation'),
    WorkGlobalSnackbar: () =>
      import('@/components/global_components/snackbar/WorkGlobalSnackbar'),
    WorkBeforeLeaveDialog,
    WorkGlobalWarningDialog,
    WorkSidebar
  },
  mixins: [
    authMixin,
    dynamicData('systeminfo', 'system/information'),
  ],
  created() {
    document.addEventListener('keyup', this.keyUpHandler);
    window.addEventListener('popstate', this.handleBrowserBackClick, false);
  },
  mounted() {
    this.$root.$beforeLeaveDialog = this.$refs.beforeLeaveDialog;
    this.$root.$warningDialog = this.$refs.warningDialog;
    this.setTabTitle();
  
    store.dispatch('sidebarDrawer/setSidebarDrawerIsOpen', false);
    store.dispatch('updateListeners/resetMaintenanceModeUpdateRequired');
  },
  updated() {
    const maintenanceInformation = this.$refs.maintenanceInformation?.$el;
    if (maintenanceInformation && maintenanceInformation instanceof HTMLElement && this.resizeObserver == undefined) {
      const paddingTop = parseInt(window.getComputedStyle(maintenanceInformation).paddingTop, 10);
      const paddingBottom = parseInt(window.getComputedStyle(maintenanceInformation).paddingBottom, 10);
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          this.maintenanceInformationHeight = entry.contentRect.height + paddingTop + paddingBottom;
        }
      });
      this.resizeObserver.observe(maintenanceInformation);
    } else if (maintenanceInformation == undefined && this.resizeObserver !== undefined) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
      this.maintenanceInformationHeight = 0;
    }
  },
  beforeDestroy() {
    this.cleanup();
  },
  deactivated() {
    this.cleanup();
  },
  computed: {
    ...mapState(['activeModule']),
    ...mapGetters('sidebarDrawer', ['isSidebarDrawerOpen']),
    ...mapGetters('updateListeners', ['getMaintenanceModeUpdateRequired', 'getWebsocketUpdateRequired']),
    name() {
      return this.data;
    },
    useTsColorTheme() {
      return this.systeminfo?.useTsTheme
    },
    systemMode() {
      return this.systeminfo?.mode;
    },
    applicationName() {
      return this.systeminfo?.applicationName;
    },
    isInternalPath() {
      return this.isAuthenticated && !this.isExternPath;
    },
    isExternPath() {
      let token = cookies.get('token');
      return this.$route.path.includes('/extern/') || token?.startsWith('-extern-');
    },
  },
  data() {
    return {
      maintenanceInformationHeight: 0,
      customLogoTestPath: CUSTOM_LOGO_TEST_PATH,
      customLogoProdPath: CUSTOM_LOGO_PROD_PATH,
      customerInstance: CUSTOMER_INSTANCE_PATH,
      resizeObserver: undefined,
      openSidebar: false
    };
  },
  methods: {
    ...mapActions('routingHistory', [
      'redirectToLastHistoryObject',
      'setBrowserBackClicked',
    ]),
    ...mapActions('globalEventListeners', ['keyUpHandler']),
    ...mapActions('updateListeners', ['resetMaintenanceModeUpdateRequired']),
    getMainClass() {
      if (!this.isAuthenticated || this.isExternPath) {
        return 'public';
      } else {
        return '';
      }
    },
    setTabTitle() {
      document.title = this.applicationName ?? this.$t('LoginView.title.work');
    },
    /*******************************************
     * BROWSER EVENTS
     *******************************************/
    handleBrowserBackClick() {
      // see also routing-history.js and router/index.js
      if (this.activeModule.isWindow) {
        this.setBrowserBackClicked();
      }
    },
    cleanup() {
      document.removeEventListener('keyup', this.keyUpHandler);
      window.removeEventListener('popstate', this.handleBrowserBackClick, false);
      this.resizeObserver.disconnect();
    },
  },
  watch: {
    applicationName: {
      handler() {
        this.setTabTitle();
      },
      immediate: true
    },
    useTsColorTheme: {

    },
  },
};
</script>

<style lang='scss' src='../../../src/styles/app.scss' />
